import type { ComponentChildren, ComponentProps } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createPlaceMarkerElement } from './PlaceMarker';
import { addMissingStyleImageFallback } from './styleImageFallback';
import { createStopMarkerElement, isStopMarkerKind } from './StopMarker';
import styles from './Map.module.css';

const DEFAULT_MAP_STYLE_URL =
  'https://styles.passengermaps.com/styles/Passenger/v3.1/style.json';
const MAPLIBRE_STYLE_TAG = 'data-passenger-maplibre-css';
const LINES_SOURCE_ID = 'widget-lines';
const LINES_BORDER_LAYER_ID = 'widget-lines-border-layer';
const LINES_LAYER_ID = 'widget-lines-layer';
const LINE_ENDPOINTS_LAYER_ID = 'widget-line-endpoints-layer';
const DEFAULT_LINE_COLOR = '#005f8f';
const DEFAULT_LINE_WIDTH = 5;
const DEFAULT_LINE_BORDER_COLOR = '#ffffff';

// We don't currently have map tiles below zoom level 5 (i.e. more zoomed out than the whole of the UK), so keep the map clamped here.
// If we add more zoomed-out tiles later then we can lower this value.
const MIN_MAP_ZOOM = 5;

type Coordinate = [number, number];
type MarkerKind =
  | 'destination'
  | 'journey-stop'
  | 'location'
  | 'origin'
  | 'stop';

interface LinesFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: {
      id: string;
      color?: string;
      borderColor?: string;
      width?: number;
      borderWidth?: number;
      endpointColor?: string;
      endpointRadius?: number;
      endpointBorderColor?: string;
      endpointBorderWidth?: number;
      pointColor?: string;
      pointRadius?: number;
      pointBorderColor?: string;
      pointBorderWidth?: number;
    };
    geometry:
      | {
          type: 'LineString';
          coordinates: Coordinate[];
        }
      | {
          type: 'Point';
          coordinates: Coordinate;
        };
  }>;
}

export interface MapLine {
  id: string;
  coordinates: Coordinate[];
  color?: string;
  borderColor?: string;
  width?: number;
  borderWidth?: number;
  showEndpoints?: boolean;
  endpointColor?: string;
  endpointRadius?: number;
  endpointBorderColor?: string;
  endpointBorderWidth?: number;
}

export interface MapPoint {
  id: string;
  coordinate: Coordinate;
  color?: string;
  radius?: number;
  borderColor?: string;
  borderWidth?: number;
}

export interface MapMarker {
  id: string;
  coordinate: Coordinate;
  kind?: MarkerKind;
  name?: string;
  mode?: string;
  bearing?: string;
}

export type MapBounds = [string, string];

export interface MapViewport {
  bounds: MapBounds;
  zoom: number;
}

export interface MapProps extends ComponentProps<'section'> {
  lines?: MapLine[];
  points?: MapPoint[];
  markers?: MapMarker[];
  fitCoordinates?: Coordinate[];
  initialCenter?: Coordinate;
  initialZoom?: number;
  singleCoordinateZoom?: number;
  onBoundsChange?: (bounds: MapBounds) => void;
  onViewportChange?: (viewport: MapViewport) => void;
  onMarkerSelect?: (markerId: string) => void;
}

interface MarkerInstanceRecord {
  instance: any;
  marker: MapMarker;
}

function toLatLngString(lat: number, lng: number): string {
  return `${lat},${lng}`;
}

function boundsToCorners(bounds: {
  getNorth: () => number;
  getWest: () => number;
  getSouth: () => number;
  getEast: () => number;
}): MapBounds {
  const topLeft = toLatLngString(bounds.getNorth(), bounds.getWest());
  const bottomRight = toLatLngString(bounds.getSouth(), bounds.getEast());

  return [topLeft, bottomRight];
}

function createPointFeature(
  id: string,
  coordinate: Coordinate,
  properties: Omit<
    LinesFeatureCollection['features'][number]['properties'],
    'id'
  >,
) {
  return {
    type: 'Feature' as const,
    properties: {
      id,
      ...properties,
    },
    geometry: {
      type: 'Point' as const,
      coordinates: coordinate,
    },
  };
}

function buildLinesFeatureCollection(
  lines: MapLine[],
  points: MapPoint[],
): LinesFeatureCollection {
  const pointKeys = new Set<string>();

  const addUniquePointFeature = (
    features: LinesFeatureCollection['features'],
    feature: LinesFeatureCollection['features'][number],
  ) => {
    const {
      geometry: { coordinates },
      properties,
    } = feature;
    const pointKey = [
      coordinates[0],
      coordinates[1],
      properties.pointColor,
      properties.pointRadius,
      properties.pointBorderColor,
      properties.pointBorderWidth,
      properties.endpointColor,
      properties.endpointRadius,
      properties.endpointBorderColor,
      properties.endpointBorderWidth,
    ].join('|');

    if (pointKeys.has(pointKey)) {
      return;
    }

    pointKeys.add(pointKey);
    features.push(feature);
  };

  return {
    type: 'FeatureCollection',
    features: [
      ...lines.flatMap((line) => {
        const lineProperties = {
          id: line.id,
          color: line.color,
          borderColor: line.borderColor,
          width: line.width,
          borderWidth: line.borderWidth,
          endpointColor: line.endpointColor,
          endpointRadius: line.endpointRadius,
          endpointBorderColor: line.endpointBorderColor,
          endpointBorderWidth: line.endpointBorderWidth,
        };
        const features: LinesFeatureCollection['features'] = [
          {
            type: 'Feature',
            properties: lineProperties,
            geometry: {
              type: 'LineString',
              coordinates: line.coordinates,
            },
          },
        ];

        if (!line.showEndpoints || line.coordinates.length === 0) {
          return features;
        }

        const startCoordinate = line.coordinates[0];
        const endCoordinate = line.coordinates[line.coordinates.length - 1];

        addUniquePointFeature(
          features,
          createPointFeature(line.id, startCoordinate, lineProperties),
        );

        if (!hasSameCoordinate(startCoordinate, endCoordinate)) {
          addUniquePointFeature(
            features,
            createPointFeature(line.id, endCoordinate, lineProperties),
          );
        }

        return features;
      }),
      ...points.flatMap((point) => {
        const features: LinesFeatureCollection['features'] = [];

        addUniquePointFeature(
          features,
          createPointFeature(point.id, point.coordinate, {
            pointColor: point.color,
            pointRadius: point.radius,
            pointBorderColor: point.borderColor,
            pointBorderWidth: point.borderWidth,
          }),
        );

        return features;
      }),
    ],
  };
}

function isValidCoordinate(coordinate: Coordinate): boolean {
  const [lng, lat] = coordinate;

  return (
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    lng >= -180 &&
    lng <= 180 &&
    lat >= -90 &&
    lat <= 90
  );
}

function filterValidMarkers(markers: MapMarker[]): MapMarker[] {
  const validMarkers = markers.filter((marker) =>
    isValidCoordinate(marker.coordinate),
  );

  if (validMarkers.length !== markers.length) {
    console.warn('[Map] Dropping markers with invalid coordinates', {
      total: markers.length,
      valid: validMarkers.length,
      invalid: markers
        .filter((marker) => !isValidCoordinate(marker.coordinate))
        .map((marker) => ({ id: marker.id, coordinate: marker.coordinate })),
    });
  }

  return validMarkers;
}

function filterValidPoints(points: MapPoint[]): MapPoint[] {
  const validPoints = points.filter((point) =>
    isValidCoordinate(point.coordinate),
  );

  if (validPoints.length !== points.length) {
    console.warn('[Map] Dropping points with invalid coordinates', {
      total: points.length,
      valid: validPoints.length,
      invalid: points
        .filter((point) => !isValidCoordinate(point.coordinate))
        .map((point) => ({ id: point.id, coordinate: point.coordinate })),
    });
  }

  return validPoints;
}

function filterValidCoordinates(coordinates: Coordinate[]): Coordinate[] {
  const validCoordinates = coordinates.filter((coordinate) =>
    isValidCoordinate(coordinate),
  );

  if (validCoordinates.length !== coordinates.length) {
    console.warn('[Map] Dropping invalid fit coordinates', {
      total: coordinates.length,
      valid: validCoordinates.length,
      invalid: coordinates.filter(
        (coordinate) => !isValidCoordinate(coordinate),
      ),
    });
  }

  return validCoordinates;
}

function coordinatesKey(coordinates: Coordinate[]): string {
  return coordinates.map(([lng, lat]) => `${lng},${lat}`).join('|');
}

function hasSameCoordinate(left: Coordinate, right: Coordinate): boolean {
  return left[0] === right[0] && left[1] === right[1];
}

function hasSameMarkerAppearance(left: MapMarker, right: MapMarker): boolean {
  return (
    left.kind === right.kind &&
    left.name === right.name &&
    left.mode === right.mode &&
    left.bearing === right.bearing
  );
}

function areEquivalentMarkers(left: MapMarker, right: MapMarker): boolean {
  return (
    left.id === right.id &&
    hasSameCoordinate(left.coordinate, right.coordinate) &&
    hasSameMarkerAppearance(left, right)
  );
}

function injectMaplibreCss(
  root: Document | ShadowRoot | HTMLElement,
  cssText: string,
): void {
  const existingStyle = root.querySelector<HTMLStyleElement>(
    `style[${MAPLIBRE_STYLE_TAG}]`,
  );

  if (existingStyle) {
    return;
  }

  const style = document.createElement('style');
  style.setAttribute(MAPLIBRE_STYLE_TAG, 'true');
  style.textContent = cssText;
  root.appendChild(style);
}

function createMarkerElement(
  marker: MapMarker,
  onSelect?: (markerId: string) => void,
): HTMLElement {
  if (isStopMarkerKind(marker.kind)) {
    return createStopMarkerElement(marker, onSelect);
  }

  return createPlaceMarkerElement(marker, onSelect);
}

export function Map({
  lines = [],
  points = [],
  markers = [],
  fitCoordinates = [],
  initialCenter,
  initialZoom = 13,
  singleCoordinateZoom = 14,
  onBoundsChange,
  onViewportChange,
  onMarkerSelect,
  children,
  className = '',
  ...props
}: MapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const maplibreRef = useRef<any>(null);
  const validPoints = useMemo(() => filterValidPoints(points), [points]);
  const validMarkers = useMemo(() => filterValidMarkers(markers), [markers]);
  const pointCoordinates = useMemo(
    () => validPoints.map((point) => point.coordinate),
    [validPoints],
  );
  const markerCoordinates = useMemo(
    () => validMarkers.map((marker) => marker.coordinate),
    [validMarkers],
  );
  const effectiveFitCoordinates = useMemo(
    () =>
      filterValidCoordinates(
        fitCoordinates.length > 0
          ? fitCoordinates
          : [...pointCoordinates, ...markerCoordinates],
      ),
    [fitCoordinates, markerCoordinates, pointCoordinates],
  );
  const markersRef = useRef<MapMarker[]>(validMarkers);
  const markerInstancesRef = useRef<
    globalThis.Map<string, MarkerInstanceRecord>
  >(new globalThis.Map());
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onViewportChangeRef = useRef(onViewportChange);
  const onMarkerSelectRef = useRef(onMarkerSelect);
  const lastAppliedFitKeyRef = useRef('');
  const [mapError, setMapError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  markersRef.current = validMarkers;
  onBoundsChangeRef.current = onBoundsChange;
  onViewportChangeRef.current = onViewportChange;
  onMarkerSelectRef.current = onMarkerSelect;

  const applyFitToCoordinates = (
    map: any,
    maplibregl: any,
    coordinates: Coordinate[],
  ): void => {
    if (coordinates.length === 0) {
      return;
    }

    const fitKey = coordinatesKey(coordinates);
    if (fitKey === lastAppliedFitKeyRef.current) {
      return;
    }

    lastAppliedFitKeyRef.current = fitKey;

    if (coordinates.length === 1) {
      map.jumpTo({ center: coordinates[0], zoom: singleCoordinateZoom });
      return;
    }

    const bounds = new maplibregl.LngLatBounds();
    for (const coordinate of coordinates) {
      bounds.extend(coordinate);
    }

    map.fitBounds(bounds, {
      padding: 32,
      duration: 0,
    });
  };

  const clearMarkerInstances = (): void => {
    for (const { instance } of markerInstancesRef.current.values()) {
      instance.remove();
    }

    markerInstancesRef.current.clear();
  };

  const createMapMarkerInstance = (
    map: any,
    maplibregl: any,
    marker: MapMarker,
  ) => {
    const element = createMarkerElement(marker, (markerId) => {
      onMarkerSelectRef.current?.(markerId);
    });

    return new maplibregl.Marker({
      element,
      anchor: 'bottom',
    })
      .setLngLat(marker.coordinate)
      .addTo(map);
  };

  const renderMarkers = (
    map: any,
    maplibregl: any,
    nextMarkers: MapMarker[],
  ) => {
    const nextMarkerIds = new Set(nextMarkers.map((marker) => marker.id));

    for (const [markerId, record] of markerInstancesRef.current) {
      if (nextMarkerIds.has(markerId)) {
        continue;
      }

      record.instance.remove();
      markerInstancesRef.current.delete(markerId);
    }

    for (const marker of nextMarkers) {
      const existingRecord = markerInstancesRef.current.get(marker.id);

      if (!existingRecord) {
        markerInstancesRef.current.set(marker.id, {
          instance: createMapMarkerInstance(map, maplibregl, marker),
          marker,
        });
        continue;
      }

      if (areEquivalentMarkers(existingRecord.marker, marker)) {
        continue;
      }

      if (hasSameMarkerAppearance(existingRecord.marker, marker)) {
        existingRecord.instance.setLngLat(marker.coordinate);
        existingRecord.marker = marker;
        continue;
      }

      existingRecord.instance.remove();
      markerInstancesRef.current.set(marker.id, {
        instance: createMapMarkerInstance(map, maplibregl, marker),
        marker,
      });
    }
  };

  useEffect(() => {
    const container = mapContainerRef.current;

    if (!container) {
      return;
    }

    if (navigator.userAgent.includes('jsdom')) {
      return;
    }

    let isDisposed = false;
    let hasInitialisedStyle = false;
    let resizeObserver: ResizeObserver | null = null;

    setIsMapReady(false);

    const initialiseMap = async () => {
      try {
        const [{ default: maplibregl }, { default: maplibreCss }] =
          await Promise.all([
            import('maplibre-gl'),
            import('maplibre-gl/dist/maplibre-gl.css?inline'),
          ]);

        if (isDisposed) {
          return;
        }

        maplibreRef.current = maplibregl;

        const rootNode = container.getRootNode();
        if (rootNode instanceof ShadowRoot) {
          injectMaplibreCss(rootNode, maplibreCss);
        } else {
          injectMaplibreCss(document.head, maplibreCss);
        }

        const map = new maplibregl.Map({
          container,
          style: import.meta.env.VITE_MAP_STYLE_URL ?? DEFAULT_MAP_STYLE_URL,
          center: initialCenter,
          minZoom: MIN_MAP_ZOOM,
          zoom: initialCenter ? initialZoom : undefined,
        });

        mapRef.current = map;

        // Don't warn the user or tests about missing sprites. Eg
        // maplibre-gl.js:42 Image "parking_11" could not be loaded. Please make sure you have added the image with map.addImage() or a "sprite" property in your style. You can provide missing images by listening for the "styleimagemissing" map event.
        // This can be removed when the map style is updated to either include the sprites, or remove references to them. PT-9881
        map.on('styleimagemissing', (event: { id?: string }) => {
          addMissingStyleImageFallback(map, event.id);
        });

        const handleMapLoaded = () => {
          if (hasInitialisedStyle) {
            return;
          }

          hasInitialisedStyle = true;

          if (isDisposed) {
            return;
          }

          map.addSource(LINES_SOURCE_ID, {
            type: 'geojson',
            data: buildLinesFeatureCollection(lines, validPoints),
          });

          map.addLayer({
            id: LINES_BORDER_LAYER_ID,
            type: 'line',
            source: LINES_SOURCE_ID,
            filter: ['==', '$type', 'LineString'],
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
            },
            paint: {
              'line-color': [
                'coalesce',
                ['get', 'borderColor'],
                DEFAULT_LINE_BORDER_COLOR,
              ],
              'line-width': ['coalesce', ['get', 'borderWidth'], 0],
            },
          });

          map.addLayer({
            id: LINES_LAYER_ID,
            type: 'line',
            source: LINES_SOURCE_ID,
            filter: ['==', '$type', 'LineString'],
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
            },
            paint: {
              'line-color': ['coalesce', ['get', 'color'], DEFAULT_LINE_COLOR],
              'line-width': ['coalesce', ['get', 'width'], DEFAULT_LINE_WIDTH],
            },
          });

          map.addLayer({
            id: LINE_ENDPOINTS_LAYER_ID,
            type: 'circle',
            source: LINES_SOURCE_ID,
            filter: ['==', '$type', 'Point'],
            paint: {
              'circle-color': [
                'coalesce',
                ['get', 'pointColor'],
                ['get', 'endpointColor'],
                ['get', 'color'],
                DEFAULT_LINE_COLOR,
              ],
              'circle-radius': [
                'coalesce',
                ['get', 'pointRadius'],
                ['get', 'endpointRadius'],
                0,
              ],
              'circle-stroke-color': [
                'coalesce',
                ['get', 'pointBorderColor'],
                ['get', 'endpointBorderColor'],
                ['get', 'borderColor'],
                DEFAULT_LINE_BORDER_COLOR,
              ],
              'circle-stroke-width': [
                'coalesce',
                ['get', 'pointBorderWidth'],
                ['get', 'endpointBorderWidth'],
                0,
              ],
            },
          });

          renderMarkers(map, maplibregl, markersRef.current);

          const emitViewport = () => {
            const currentBounds = map.getBounds();
            const bounds = boundsToCorners(currentBounds);
            const viewport = {
              bounds,
              zoom: map.getZoom(),
            };

            onBoundsChangeRef.current?.(bounds);
            onViewportChangeRef.current?.(viewport);
          };

          map.on('moveend', emitViewport);
          emitViewport();

          const coordinates =
            effectiveFitCoordinates.length > 0
              ? effectiveFitCoordinates
              : markersRef.current.map((marker) => marker.coordinate);

          applyFitToCoordinates(map, maplibregl, coordinates);
          setIsMapReady(true);
        };

        map.on('load', handleMapLoaded);

        if (map.isStyleLoaded()) {
          handleMapLoaded();
        }

        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => {
            map.resize();
          });
          resizeObserver.observe(container);
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to load map view right now.';
        setMapError(message);
      }
    };

    void initialiseMap();

    return () => {
      isDisposed = true;
      setIsMapReady(false);
      lastAppliedFitKeyRef.current = '';
      resizeObserver?.disconnect();
      clearMarkerInstances();
      mapRef.current?.remove();
      mapRef.current = null;
      maplibreRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }

    const map = mapRef.current;

    if (!map) {
      return;
    }

    const linesSource = map.getSource(LINES_SOURCE_ID);
    if (linesSource && 'setData' in linesSource) {
      linesSource.setData(buildLinesFeatureCollection(lines, validPoints));
    }
  }, [isMapReady, lines, validPoints]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }

    const map = mapRef.current;

    if (!map) {
      return;
    }

    renderMarkers(map, maplibreRef.current, validMarkers);
  }, [isMapReady, validMarkers]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }

    const map = mapRef.current;
    const maplibregl = maplibreRef.current;

    if (!map || !maplibregl) {
      return;
    }

    applyFitToCoordinates(map, maplibregl, effectiveFitCoordinates);
  }, [effectiveFitCoordinates, isMapReady]);

  const sectionClassName = [styles.wrapper, className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={sectionClassName} {...props}>
      <div ref={mapContainerRef} className={styles.canvas} />
      {children as ComponentChildren}
      {mapError && <p className={styles.error}>{mapError}</p>}
    </section>
  );
}
